import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import MyLocationIcon from '@material-ui/icons/MyLocation'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import WaterAccessFormDialog from './WaterAccessFormDialog'

const DEFAULT_CENTER = [33.55, -112.0]

const CONNECTION_TYPE_LABELS = {
  hose_bib: 'Hose Bib',
  hydrant: 'Fire Hydrant',
  overhead_dump: 'Overhead Dump',
  pond_lake: 'Pond / Lake',
  tank_cistern: 'Tank / Cistern',
  spigot: 'Spigot',
  other: 'Other'
}

const SPEED_TIER_COLORS = {
  fast: '#e67700',
  medium: '#2d5a3d',
  slow: '#5d6c64'
}

const COST_TYPE_LABELS = {
  free: 'Free',
  paid: 'Paid',
  permit_required: 'Permit Req\'d',
  unknown: 'Unknown'
}

const COST_TYPE_COLORS = {
  free: '#6a9',
  paid: '#e67700',
  permit_required: '#b3261e',
  unknown: '#999'
}

const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(1) },
  controlsCard: {
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  splitContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    minHeight: 600
  },
  listPanel: {
    width: 360,
    minWidth: 360,
    background: '#fff',
    borderRadius: 8,
    border: '1px solid rgba(205, 216, 209, 0.9)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflowY: 'auto',
    maxHeight: 680
  },
  listItem: {
    padding: '10px 14px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { background: '#f8faf8' }
  },
  listItemSelected: {
    background: '#e8f0ea !important'
  },
  listItemName: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#20342b'
  },
  listItemMeta: {
    fontSize: '0.75rem',
    color: '#5d6c64',
    marginTop: 2
  },
  listItemCost: {
    fontSize: '0.75rem',
    marginTop: 1
  },
  mapCard: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  mapContainer: {
    width: '100%',
    height: 680,
    backgroundColor: '#e8efe4'
  },
  metricCard: {
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: '#20342b'
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: '#5d6c64',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    color: '#5d6c64'
  },
  actionButtons: {
    display: 'flex',
    gap: 4,
    marginTop: 4
  }
}))

function buildMarkerIcon (speedTier) {
  const color = SPEED_TIER_COLORS[speedTier] || SPEED_TIER_COLORS.slow
  return L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  })
}

// Consider stale if not verified in last 90 days
function isStale (lastVerifiedAt) {
  if (!lastVerifiedAt) return true
  const daysSince = (Date.now() - new Date(lastVerifiedAt).getTime()) / (1000 * 60 * 60 * 24)
  return daysSince > 90
}

function buildUserLocationIcon () {
  return L.divIcon({
    className: '',
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#4285f4;border:2px solid #fff;box-shadow:0 0 0 4px rgba(66,133,244,0.2);"></div>',
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  })
}

const WaterAccessManager = ({ apiBase, canEdit, showNearMe }) => {
  const classes = useStyles()

  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({ total: 0, byType: {} })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [connectionTypeFilter, setConnectionTypeFilter] = useState('')
  const [costTypeFilter, setCostTypeFilter] = useState('')

  const [nearMeActive, setNearMeActive] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [geoError, setGeoError] = useState('')

  const [selectedItem, setSelectedItem] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editItem, setEditItem] = useState(null)

  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])
  const userMarkerRef = useRef(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 250)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      let response
      if (nearMeActive && userLocation) {
        response = await axios.get(`${apiBase}/nearby`, {
          params: {
            lat: userLocation[0],
            lng: userLocation[1],
            radius: 80000,
            ...(connectionTypeFilter ? { connectionType: connectionTypeFilter } : {})
          }
        })
      } else {
        response = await axios.get(apiBase, {
          params: {
            ...(search ? { search } : {}),
            ...(connectionTypeFilter ? { connectionType: connectionTypeFilter } : {}),
            ...(costTypeFilter ? { costType: costTypeFilter } : {})
          }
        })
      }

      setItems(response.data?.items || [])
      if (response.data?.summary) setSummary(response.data.summary)
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load water access points')
    } finally {
      setLoading(false)
    }
  }, [apiBase, search, connectionTypeFilter, costTypeFilter, nearMeActive, userLocation])

  useEffect(() => { loadData() }, [loadData])

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 11,
      zoomControl: true
    })

    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      attribution: ''
    }).addTo(map)

    mapRef.current = map

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Update markers
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    markersRef.current.forEach(m => map.removeLayer(m))
    markersRef.current = []

    const validItems = items.filter(i =>
      i.location?.coordinates?.length === 2 &&
      i.location.coordinates[0] !== 0 &&
      i.location.coordinates[1] !== 0
    )

    const bounds = []

    validItems.forEach(item => {
      const [lon, lat] = item.location.coordinates
      const tier = item.speedTier || 'slow'
      const marker = L.marker([lat, lon], { icon: buildMarkerIcon(tier) })

      const costLabel = item.costNotes || COST_TYPE_LABELS[item.costType] || ''
      const distLabel = item.distance != null ? ` &middot; ${item.distance} mi` : ''

      marker.bindPopup(
        `<div style="min-width:160px">` +
        `<div style="font-weight:600;font-size:13px;margin-bottom:4px">${item.name}</div>` +
        `<div style="font-size:11px;color:#555">${CONNECTION_TYPE_LABELS[item.connectionType] || item.connectionType}${distLabel}</div>` +
        (costLabel ? `<div style="font-size:11px;color:${COST_TYPE_COLORS[item.costType] || '#999'};margin-top:2px">${costLabel}</div>` : '') +
        (item.contactName ? `<div style="font-size:11px;color:#555;margin-top:4px">Contact: ${item.contactName}</div>` : '') +
        `</div>`
      )

      marker.on('click', () => setSelectedItem(item))
      marker.addTo(map)
      markersRef.current.push(marker)
      bounds.push([lat, lon])
    })

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
    } else {
      map.setView(DEFAULT_CENTER, 11)
    }
  }, [items])

  // Update user location marker
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (userMarkerRef.current) {
      map.removeLayer(userMarkerRef.current)
      userMarkerRef.current = null
    }

    if (userLocation) {
      const marker = L.marker(userLocation, { icon: buildUserLocationIcon() })
      marker.bindPopup('<div style="font-size:12px;font-weight:600">Your Location</div>')
      marker.addTo(map)
      userMarkerRef.current = marker
    }
  }, [userLocation])

  // Near Me handler
  const handleNearMe = () => {
    if (nearMeActive) {
      setNearMeActive(false)
      setUserLocation(null)
      setGeoError('')
      return
    }

    if (!navigator.geolocation) {
      setGeoError('Geolocation not supported')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation([pos.coords.latitude, pos.coords.longitude])
        setNearMeActive(true)
        setGeoError('')
      },
      (err) => {
        setGeoError('Unable to get location: ' + err.message)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  // CRUD handlers
  const handleSave = async (payload, id) => {
    if (id) {
      await axios.put(`${apiBase}/${id}`, payload)
    } else {
      await axios.post(apiBase, payload)
    }
    loadData()
  }

  const handleVerify = async (item) => {
    try {
      await axios.post(`${apiBase}/${item._id}/verify`, {})
      loadData()
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to verify')
    }
  }

  const handleLogUsage = async (item) => {
    try {
      await axios.post(`${apiBase}/${item._id}/log-usage`, {})
      loadData()
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to log usage')
    }
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return
    try {
      await axios.delete(`${apiBase}/${item._id}`)
      loadData()
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete')
    }
  }

  const handleListItemClick = (item) => {
    setSelectedItem(item)
    // Open popup on map
    const marker = markersRef.current.find(m => {
      const ll = m.getLatLng()
      return ll.lat === item.location?.coordinates?.[1] && ll.lng === item.location?.coordinates?.[0]
    })
    if (marker) {
      marker.openPopup()
      mapRef.current?.panTo(marker.getLatLng())
    }
  }

  // Summary metrics
  const metrics = useMemo(() => [
    { label: 'Total Points', value: summary.total || 0, accent: '#2d5a3d' },
    { label: 'Fast (Dump)', value: summary.byType?.overhead_dump || 0, accent: '#e67700' },
    { label: 'Medium (Hydrant/Tank)', value: (summary.byType?.hydrant || 0) + (summary.byType?.tank_cistern || 0), accent: '#2d5a3d' },
    { label: 'Slow (Bib/Other)', value: (summary.byType?.hose_bib || 0) + (summary.byType?.spigot || 0) + (summary.byType?.pond_lake || 0) + (summary.byType?.other || 0), accent: '#5d6c64' }
  ], [summary])

  return (
    <div className={classes.root}>
      {/* Metrics */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {metrics.map(m => (
          <Grid item xs={6} sm={3} key={m.label}>
            <div className={classes.metricCard}>
              <div className={classes.metricLabel}>{m.label}</div>
              <div className={classes.metricValue} style={{ color: m.accent }}>{m.value}</div>
            </div>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card className={classes.controlsCard}>
        <CardContent style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search locations..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            variant="outlined"
            style={{ minWidth: 200 }}
            disabled={nearMeActive}
          />
          <TextField
            select
            value={connectionTypeFilter}
            onChange={(e) => setConnectionTypeFilter(e.target.value)}
            size="small"
            variant="outlined"
            style={{ minWidth: 150 }}
            label="Type"
          >
            <MenuItem value="">All Types</MenuItem>
            {Object.entries(CONNECTION_TYPE_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </TextField>
          {!nearMeActive && (
            <TextField
              select
              value={costTypeFilter}
              onChange={(e) => setCostTypeFilter(e.target.value)}
              size="small"
              variant="outlined"
              style={{ minWidth: 130 }}
              label="Cost"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="permit_required">Permit Req'd</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </TextField>
          )}
          {showNearMe && (
            <Button
              variant={nearMeActive ? 'contained' : 'outlined'}
              size="small"
              startIcon={<MyLocationIcon />}
              onClick={handleNearMe}
              style={nearMeActive ? { backgroundColor: '#4285f4', color: '#fff', textTransform: 'none' } : { textTransform: 'none' }}
            >
              {nearMeActive ? 'Near Me (On)' : 'Near Me'}
            </Button>
          )}
          {geoError && <Typography variant="caption" color="error">{geoError}</Typography>}
          <div style={{ flex: 1 }} />
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setEditItem(null); setFormOpen(true) }}
              style={{ backgroundColor: '#2d5a3d', color: '#fff', textTransform: 'none' }}
            >
              Add Point
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>{error}</Typography>
      )}

      {/* Split: List + Map */}
      <div className={classes.splitContainer}>
        {/* List panel */}
        <div className={classes.listPanel}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary">
                {nearMeActive ? 'No water access points found nearby.' : 'No water access points found.'}
              </Typography>
            </div>
          ) : (
            items.map(item => {
              const isSelected = selectedItem?._id === item._id
              return (
                <div
                  key={item._id}
                  className={`${classes.listItem} ${isSelected ? classes.listItemSelected : ''}`}
                  onClick={() => handleListItemClick(item)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className={classes.listItemName}>{item.name}</div>
                    <Chip
                      label={CONNECTION_TYPE_LABELS[item.connectionType] || item.connectionType}
                      size="small"
                      style={{
                        fontSize: '0.65rem',
                        height: 20,
                        backgroundColor: SPEED_TIER_COLORS[item.speedTier] || '#5d6c64',
                        color: '#fff'
                      }}
                    />
                  </div>
                  <div className={classes.listItemMeta}>
                    {item.address?.formatted || item.address?.city || ''}
                    {item.distance != null && ` · ${item.distance} mi`}
                  </div>
                  <div
                    className={classes.listItemCost}
                    style={{ color: COST_TYPE_COLORS[item.costType] || '#999' }}
                  >
                    {item.costNotes || COST_TYPE_LABELS[item.costType] || ''}
                    {item.estimatedTotalMinutes != null && (
                      <span style={{ color: '#5d6c64', marginLeft: 8 }}>
                        ~{item.estimatedTotalMinutes} min round trip
                      </span>
                    )}
                  </div>
                  {/* Staleness + usage indicators */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                    {item.lastVerifiedAt ? (
                      <Tooltip title={`Verified ${new Date(item.lastVerifiedAt).toLocaleDateString()}${item.lastVerifiedByName ? ` by ${item.lastVerifiedByName}` : ''}`}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.7rem', color: isStale(item.lastVerifiedAt) ? '#e65100' : '#2e7d32' }}>
                          {isStale(item.lastVerifiedAt) ? <WarningIcon style={{ fontSize: 12 }} /> : <CheckCircleIcon style={{ fontSize: 12 }} />}
                          {isStale(item.lastVerifiedAt) ? 'Stale' : 'Verified'}
                        </span>
                      </Tooltip>
                    ) : (
                      <span style={{ fontSize: '0.7rem', color: '#999' }}>Unverified</span>
                    )}
                    {item.usage?.totalUses > 0 && (
                      <span style={{ fontSize: '0.7rem', color: '#5d6c64' }}>
                        · {item.usage.totalUses} use{item.usage.totalUses !== 1 ? 's' : ''}
                      </span>
                    )}
                    {item.photos?.length > 0 && (
                      <span style={{ fontSize: '0.7rem', color: '#5d6c64', display: 'flex', alignItems: 'center', gap: 2 }}>
                        · <PhotoCameraIcon style={{ fontSize: 11 }} /> {item.photos.length}
                      </span>
                    )}
                  </div>
                  {canEdit && isSelected && (
                    <div className={classes.actionButtons}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setEditItem(item); setFormOpen(true) }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark Verified">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleVerify(item) }}
                        >
                          <CheckCircleIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Log Usage">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleLogUsage(item) }}
                        >
                          <span style={{ fontSize: 14, fontWeight: 700 }}>+1</span>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDelete(item) }}
                        >
                          <DeleteIcon style={{ fontSize: 16, color: '#b3261e' }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Map */}
        <div className={classes.mapCard}>
          <div ref={mapContainerRef} className={classes.mapContainer} />
        </div>
      </div>

      {/* Form dialog */}
      <WaterAccessFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditItem(null) }}
        onSave={handleSave}
        editItem={editItem}
        apiBase={apiBase}
      />
    </div>
  )
}

export default WaterAccessManager
